import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  ListDataBrokersQuery,
  UpsertDataBrokerMutation,
  UpsertDataBrokerMutationVariables,
} from "~frontend/__generated__/graphql";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfErrorBanner,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import {
  LIST_DATA_BROKERS,
  UPSERT_DATA_BROKER,
} from "~frontend/graphql/dataBroker";
import { BIGQUERY_LOCATION_OPTIONS } from "~frontend/util/bigQueryLocations";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

type DataBroker = ListDataBrokersQuery["listDataBrokers"][0];

type FormValues = {
  name: string;
  slug: string;
  gcsBucket: string;
  gcsBucketPrefix: string;
  dataset: string;
  location: string;
  credentialsJSON: string;
};

const LOCATION_VALUES = BIGQUERY_LOCATION_OPTIONS.map(o => o.value) as [
  string,
  ...string[],
];

const SLUG_REGEX = /^[a-z_][a-z0-9_]{0,30}$/;
const SLUG_ERROR_MESSAGE =
  "Slug must be 1–31 characters: starting with a lowercase letter or underscore, then lowercase letters, digits, or underscores";

function slugifyName(name: string): string {
  let slug = name.toLowerCase().replace(/[^a-z0-9_]/g, "_");
  if (slug.length === 0) {
    return "";
  }
  if (/^[0-9]/.test(slug)) {
    slug = "_" + slug;
  }
  return slug.slice(0, 31);
}

function makeSchema(isUpdate: boolean) {
  return z.object({
    name: z.string().min(1, "Required"),
    slug: isUpdate
      ? z.string()
      : z.string().min(1, "Required").regex(SLUG_REGEX, SLUG_ERROR_MESSAGE),
    gcsBucket: z.string().min(1, "Required"),
    gcsBucketPrefix: z.string(),
    dataset: z.string().min(1, "Required"),
    location: z.enum(LOCATION_VALUES),
    credentialsJSON: isUpdate ? z.string() : z.string().min(1, "Required"),
  });
}

export default function DataBrokerDialog({
  open,
  onClose,
  dataBroker,
}: {
  open: boolean;
  onClose: () => void;
  dataBroker: DataBroker | null;
}): ReactElement {
  const isUpdate = !!dataBroker;
  const formatErrorMessage = useErrorMessageFormatter();

  const schema = useMemo(() => makeSchema(isUpdate), [isUpdate]);

  const { control, handleSubmit, formState, setError, setValue } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        name: dataBroker?.name ?? "",
        slug: dataBroker?.slug ?? "",
        gcsBucket: dataBroker?.settings.gcsBucket ?? "",
        gcsBucketPrefix: dataBroker?.settings.gcsBucketPrefix ?? "",
        dataset: dataBroker?.settings.dataset ?? "",
        location: dataBroker?.settings.location ?? "US",
        credentialsJSON: "",
      },
    });

  const watchedName = useWatch({ control, name: "name" });
  const slugIsDirty = !!formState.dirtyFields.slug;

  useEffect(() => {
    if (isUpdate || slugIsDirty) {
      return;
    }
    setValue("slug", slugifyName(watchedName));
  }, [watchedName, isUpdate, slugIsDirty, setValue]);

  const [upsertDataBroker] = useMutation<
    UpsertDataBrokerMutation,
    UpsertDataBrokerMutationVariables
  >(UPSERT_DATA_BROKER, {
    refetchQueries: [LIST_DATA_BROKERS],
    awaitRefetchQueries: true,
  });

  async function onSubmit(values: FormValues) {
    const { errors } = await upsertDataBroker({
      variables: {
        input: {
          id: dataBroker?.id,
          name: values.name,
          slug: values.slug,
          gcsBucket: values.gcsBucket,
          gcsBucketPrefix: values.gcsBucketPrefix,
          dataset: values.dataset,
          location: values.location,
          credentialsJSON:
            values.credentialsJSON.trim().length > 0
              ? values.credentialsJSON
              : undefined,
        },
      },
    });

    if (errors) {
      setError("root", { message: formatErrorMessage(errors) });
      return;
    }

    enqueueSnackbar(`Data broker ${isUpdate ? "updated" : "created"}`, {
      variant: "success",
    });

    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isUpdate ? "Edit Data Broker" : "Add Data Broker"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <RhfErrorBanner control={control} />
          <RhfTextField control={control} name="name" label="Name" fullWidth />
          <RhfTextField
            control={control}
            name="slug"
            label="Slug"
            fullWidth
            disabled={isUpdate}
            bottomLabel={isUpdate ? "Slug cannot be changed" : undefined}
          />
          <RhfTextField
            control={control}
            name="gcsBucket"
            label="GCS Bucket"
            fullWidth
          />
          <RhfTextField
            control={control}
            name="gcsBucketPrefix"
            label="GCS Bucket Object Prefix"
            optional
            fullWidth
          />
          <RhfTextField
            control={control}
            name="dataset"
            label="BigQuery Dataset Name"
            fullWidth
          />
          <RhfSelect
            control={control}
            name="location"
            label="BigQuery Location"
            fullWidth
          >
            {BIGQUERY_LOCATION_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </RhfSelect>
          <RhfTextField
            control={control}
            name="credentialsJSON"
            label={
              isUpdate
                ? "Credentials JSON (leave blank to keep current)"
                : "Credentials JSON"
            }
            multiline
            rows={4}
            fullWidth
            InputProps={{
              inputProps: {
                style: {
                  color: "transparent",
                  textShadow: "0 0 8px rgba(0,0,0,0.5)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={formState.isSubmitting}
            onClick={onClose}
            color="secondary"
          >
            Cancel
          </Button>
          <SubmitButton
            isSubmitting={formState.isSubmitting}
            color="primary"
            variant="contained"
            submitText={isUpdate ? "Save" : "Add"}
            loadingText="Saving..."
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
